import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { CallEntity } from './call.entity';
import { UserEntity } from './user.entity';

/**
 * Audit row for one live-call coaching session (Tier 2 — Listen /
 * Whisper / Barge). Compliance-critical: a manager covertly hearing
 * a customer call is exactly the kind of action that gets companies
 * sued in two-party-consent jurisdictions, so every session lands a
 * persistent row that can be reconstructed without trusting any
 * runtime state.
 *
 * One row per coaching attempt — mode transitions (listen → whisper
 * → barge in the same session) are tracked on the same row via the
 * latest `mode` value plus the `modeChangedAt` timestamp. Mode
 * history beyond the latest is captured implicitly by grouping rows
 * with the same `participantCallSid` if needed; in practice the
 * latest mode is what auditors care about (escalation matters more
 * than the exact intermediate steps).
 *
 * **Why the participant CallSid is on the row**: when ending a
 * session we need to call `participants(sid).remove()` on the live
 * conference. Storing the SID lets the end path work without
 * re-querying Twilio for "who is the manager in this conference."
 *
 * **Auto-end on call termination**: when the underlying call's
 * conference dies, all coaching sessions for that call get their
 * `endedAt` stamped automatically by the post-call hook. Sessions
 * without an explicit end via the API still close with the same
 * timestamp the customer call ended.
 */
@Index('softphone_call_coaching_call_idx', ['fkCallId'])
@Index('softphone_call_coaching_coach_idx', ['fkCoachUserId'])
@Index('softphone_call_coaching_active_idx', ['endedAt'])
@Entity('softphone_call_coaching_session')
export class SoftphoneCallCoachingSessionEntity extends BaseEntity {
	@Column('int', { name: 'fk_call__id', unsigned: true })
	public fkCallId!: number;

	/** The user (manager / supervisor) doing the coaching. */
	@Column('int', { name: 'fk_user__id_coach', unsigned: true })
	public fkCoachUserId!: number;

	/**
	 * Current coaching mode. Mutates on `updateMode` API calls so the
	 * audit trail always reflects the latest behavior of an active
	 * session.
	 *   - `listen`  — manager hears the call; rep + customer hear nothing
	 *   - `whisper` — manager hears the call; rep also hears the manager;
	 *                 customer hears nothing of the manager
	 *   - `barge`   — manager is a full third participant; everyone hears
	 *                 each other. Conference survives a rep hangup so
	 *                 the manager can effectively take the call over.
	 */
	@Column('varchar', { name: 'mode', length: 16 })
	public mode!: 'listen' | 'whisper' | 'barge';

	/** When the latest mode value above was set. Equals `startedAt`
	 *  for sessions that never changed mode. */
	@Column('datetime', { name: 'modeChangedAt' })
	public modeChangedAt!: Date;

	/**
	 * Twilio CallSid of the manager's leg in the conference. Used to
	 * issue `.participants(sid).update(...)` for mode changes and
	 * `.participants(sid).remove()` to end the session. Null only in
	 * the brief window between row insert and the participant being
	 * created (the create call returns the SID we then patch onto
	 * the row).
	 */
	@Column('varchar', {
		name: 'participantCallSid',
		length: 64,
		nullable: true,
	})
	public participantCallSid!: string | null;

	@Column('datetime', {
		name: 'startedAt',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public startedAt!: Date;

	/**
	 * Set when the session ends — either via explicit API call, the
	 * underlying call ending, or the manager dropping the leg from
	 * their browser. Null = session is still live. The active-session
	 * lookup keys off this column.
	 */
	@Column('datetime', { name: 'endedAt', nullable: true })
	public endedAt!: Date | null;

	/**
	 * Free-form reason captured at end time — `'manager-ended'`,
	 * `'call-ended'`, `'rep-hangup-listen-mode'`, `'manager-leg-dropped'`,
	 * etc. Not validated; the audit value is the human-readable label
	 * for after-the-fact review.
	 */
	@Column('varchar', { name: 'endReason', length: 64, nullable: true })
	public endReason!: string | null;

	@ManyToOne(() => CallEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_call__id', referencedColumnName: 'id' }])
	public fkCall!: CallEntity;

	@ManyToOne(() => UserEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id_coach', referencedColumnName: 'id' }])
	public fkCoachUser!: UserEntity;
}
